"use client";

import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, X } from "lucide-react";
import * as S from "./style";

interface OptionType {
  value: string;
  label: string;
  image?: string;
}

interface MultiSelectProps {
  options: OptionType[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  multiple?: boolean; // 👈 New prop
}

export const MultiSelect = ({
  options,
  selected,
  onChange,
  placeholder = "Select...",
  disabled = false,
  searchable = false,
  multiple = true, // 👈 Default to true
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggle = (value: string) => {
    if (multiple) {
      const updated = selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value];
      onChange(updated);
    } else {
      // Single select: toggle or select
      const updated = selected.includes(value) ? [] : [value];
      onChange(updated);
      setOpen(false); // Auto-close dropdown on single select
    }
  };

  const handleOutsideClick = (e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <S.Wrapper ref={ref} $disabled={disabled}>
      <S.Header onClick={() => !disabled && setOpen(!open)}>
        <S.ItemsContainer>
          {selected.length === 0 ? (
            <S.Placeholder>{placeholder}</S.Placeholder>
          ) : multiple ? (
            selected.map((val) => {
              const item = options.find((o) => o.value === val);
              return (
                <S.Tag key={val}>
                  {item?.image && (
                    <img
                      src={item.image}
                      alt=""
                      style={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  {item?.label}
                  <S.Remove
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(val);
                    }}
                  >
                    <X size={12} />
                  </S.Remove>
                </S.Tag>
              );
            })
          ) : (
            (() => {
              const selectedOption = options.find(
                (o) => o.value === selected[0]
              );
              return selectedOption ? (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {selectedOption.image && (
                    <img
                      src={selectedOption.image}
                      alt=""
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  <span>{selectedOption.label}</span>
                </div>
              ) : (
                <S.Placeholder>{placeholder}</S.Placeholder>
              );
            })()
          )}
        </S.ItemsContainer>
        <ChevronDown size={16} />
      </S.Header>

      {open && (
        <S.Dropdown>
          {searchable && (
            <S.Search
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          )}
          <S.List>
            {filtered.map((option) => (
              <S.Item
                key={option.value}
                $selected={selected.includes(option.value)}
                onClick={() => toggle(option.value)}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {option.image && (
                    <img
                      src={option.image}
                      alt=""
                      style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "1px solid #e5e7eb",
                      }}
                    />
                  )}
                  <span>{option.label}</span>
                </div>
                {selected.includes(option.value) && <Check size={16} />}
              </S.Item>
            ))}
          </S.List>
        </S.Dropdown>
      )}
    </S.Wrapper>
  );
};
